import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { FileSignature, Info, TrendingUp } from "lucide-react";
import { OFFER, offerActive, discounted, eur } from "@/lib/offer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLANS = [
  { id: "essential", name: "Essential", setup: discounted(990), monthly: 19 },
  { id: "pro", name: "Pro Direct", setup: discounted(1990), monthly: 29 },
  { id: "tailored", name: "Tailored", setup: discounted(2990), monthly: 49 },
];

const HOSTING_CREDITS = 50; // crediti/mese per sito Starter (~€9,50)

function Table({ head, rows }) {
  return (
    <div className="overflow-x-auto rounded-2xl border border-line bg-panel">
      <table className="w-full min-w-[560px] text-left text-sm">
        <thead>
          <tr className="border-b border-line">
            {head.map((h, i) => (
              <th
                key={h}
                className={`px-4 py-3 font-accent text-[10px] uppercase tracking-[0.2em] ${
                  i === 0 ? "text-stone" : "text-right text-stone"
                }`}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-b border-line/60 last:border-0">
              {r.map((c, j) => (
                <td
                  key={j}
                  className={`px-4 py-3 ${j === 0 ? "text-stone" : "text-right text-bone"} ${
                    typeof c === "string" && c.startsWith("TOT") ? "font-semibold" : ""
                  }`}
                >
                  {c}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Block({ tag, title, children }) {
  return (
    <div className="mt-12">
      <p className="flex items-center gap-2 font-accent text-[11px] uppercase tracking-[0.28em] text-terracotta">
        <TrendingUp size={13} /> {tag}
      </p>
      <h2 className="mt-3 font-serif text-2xl tracking-tight text-bone">{title}</h2>
      <div className="mt-5">{children}</div>
    </div>
  );
}

export default function BusinessPlan() {
  const [proposals, setProposals] = useState([]);

  useEffect(() => {
    axios
      .get(`${API}/proposals`)
      .then(({ data }) => setProposals(data))
      .catch(() => setProposals([]));
  }, []);

  const hostingEuro = Math.round(HOSTING_CREDITS * 0.19);

  return (
    <div>
      <div className="flex items-center gap-3">
        <Info size={20} className="text-ochre" />
        <p className="text-sm leading-relaxed text-stone">
          Stime indicative: 50 crediti ≈ 9-10 € (verifica la conversione esatta nel pannello
          Pricing Emergent). Non sono inclusi il valore del tuo tempo e le tasse.
        </p>
      </div>

      <Block tag="Costi diretti" title="Quanto costa ogni singolo sito (cliente attivo)">
        <Table
          head={["Voce", "Mensile", "Anno"]}
          rows={[
            [`Hosting sito cliente (Starter, ${HOSTING_CREDITS} crediti)`, `~${eur(hostingEuro)}`, `~${eur(hostingEuro * 12)}`],
            ["Dominio del cliente (a suo nome)", "—", `~${eur(12)}`],
            ["Email cliente (Zoho gratis / Workspace)", "€0 — 6", "€0 — 72"],
            ["Analisi IA sui prospect (universale key)", "~€0,05", "trascurabile"],
            ["TOTALE costi diretti per sito", `~${eur(hostingEuro + 1)}`, `~${eur(hostingEuro * 12 + 12 + 12)}`],
          ]}
        />
        <p className="mt-3 text-xs text-faded">
          Il dominio e la casella email del cliente si pagano dal cliente direttamente: non toccano
          il tuo margine. I tuoi costi fissi restano piano Standard, dominio del tuo sito e tua email.
        </p>
      </Block>

      <Block tag="Ricavi" title="Margine per singolo sito, primo anno">
        <Table
          head={["Piano", "Setup", "Canone 12 mesi", "Ricavo anno 1", "Costi anno 1", "Margine"]}
          rows={[
            ...PLANS.map((p) => [
              p.name,
              eur(p.setup),
              eur(p.monthly * 12),
              eur(p.setup + p.monthly * 12),
              `~${eur(hostingEuro * 12 + 24)}`,
              `~${eur(p.setup + p.monthly * 12 - hostingEuro * 12 - 24)}`,
            ]),
          ]}
        />
        <p className="mt-3 text-xs text-faded">
          {offerActive() && `Setup calcolato con ${OFFER.short} (valida fino al ${OFFER.deadlineLabel}). `}
          Margine lordo 88-96% sui costi diretti: il costo vero è il tuo tempo (stimato 12-16 h per
          Essential, 20-25 h per Pro, 35-40 h per Tailored).
        </p>
      </Block>

      <Block tag="Canoni" title="Ricavo ricorrente dal mantenimento">
        <Table
          head={["Clienti attivi", "Canoni mensili (da €19)", "Hosting (costo tuo)", "Utile canoni/mese"]}
          rows={[
            ["5 clienti", eur(95), `-${eur(hostingEuro * 5)}`, `~${eur(95 - hostingEuro * 5)}`],
            ["10 clienti", eur(190), `-${eur(hostingEuro * 10)}`, `~${eur(190 - hostingEuro * 10)}`],
            ["20 clienti", eur(380), `-${eur(hostingEuro * 20)}`, `~${eur(380 - hostingEuro * 20)}`],
          ]}
        />
        <p className="mt-3 text-xs text-faded">
          Con canone consigliato €19-49 in base al piano, l'hosting pesa 20-50% del canone: il resto
          è utile per assistenza e piccole modifiche.
        </p>
      </Block>

      <Block tag="Sostenibilità" title="Quanti siti regge con il piano Standard">
        <Table
          head={["Scenario", "Consumo crediti/mese", "Esito"]}
          rows={[
            ["3 siti Starter attivi", "150 crediti", "Circa metà dei 250 crediti Standard: margine ok"],
            ["5 siti Starter attivi", "250 crediti", "Break-even esatto del piano Standard"],
            ["Oltre 5 siti", "Servono tier superiori", "Aumenta il piano o passa il cliente a un tier superiore"],
          ]}
        />
        <p className="mt-3 text-xs text-faded">
          Regola pratica: ogni nuovo cliente porta +50 crediti/mese di costo. Aumenta il canone (o il
          tier di deployment) quando scendi sotto ~50 crediti di riserva.
        </p>
      </Block>

      <Block tag="Documenti" title="Proposte firmate digitalmente">
        {proposals.length === 0 ? (
          <p className="rounded-2xl border border-dashed border-line bg-panel p-6 text-sm text-stone">
            Nessuna proposta firmata ancora. Apri una attività qui sotto e usa «Crea proposta»:
            il cliente seleziona il piano, firma e il documento finisce qui.
          </p>
        ) : (
          <Table
            head={["Cliente", "Attività", "Piano", "Economia", "Data"]}
            rows={proposals.map((p) => [
              p.client_name,
              p.prospect_name || "—",
              p.plan_name,
              `${eur(p.setup_price)} + ${eur(p.monthly_price)}/mese`,
              new Date(p.signed_at).toLocaleDateString("it-IT"),
            ])}
          />
        )}
      </Block>
    </div>
  );
}